import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { UsageAnalytics } from './analytics';
import styles from './SettingsModal.module.css';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialSection?: SettingsSection;
}

type SettingsSection = 'general' | 'profile' | 'notifications' | 'privacy';

export const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose, initialSection = 'general' }) => {
  const { theme, setTheme } = useTheme();
  const [activeSection, setActiveSection] = useState<SettingsSection>(initialSection);

  // Update active section when initialSection changes
  React.useEffect(() => {
    if (isOpen) {
      setActiveSection(initialSection);
    }
  }, [isOpen, initialSection]);

  if (!isOpen) return null;

  const handleThemeChange = (newTheme: 'dark' | 'light') => {
    setTheme(newTheme);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'general':
        return (
          <div className={styles.generalContainer}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Appearance</h2>
            </div>

            <p className={styles.sectionDescription}>
              Choose your preferred theme for the interface
            </p>

            <div className={styles.themeOptions}>
                <button
                  className={`${styles.themeOption} ${theme === 'dark' ? styles.active : ''}`}
                  onClick={() => handleThemeChange('dark')}
                >
                  <div className={styles.themePreview} data-theme="dark">
                    <div className={styles.previewBar}></div>
                    <div className={styles.previewContent}>
                      <div className={styles.previewLine}></div>
                      <div className={styles.previewLine}></div>
                    </div>
                  </div>
                  <span className={styles.themeName}>Dark</span>
                  {theme === 'dark' && (
                    <svg className={styles.checkIcon} width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M13.485 1.431a1.473 1.473 0 012.104 2.062l-7.84 9.801a1.473 1.473 0 01-2.12.04L.431 8.138a1.473 1.473 0 012.084-2.083l4.111 4.112 6.82-8.69a.486.486 0 01.04-.045z"/>
                    </svg>
                  )}
                </button>

                <button
                  className={`${styles.themeOption} ${theme === 'light' ? styles.active : ''}`}
                  onClick={() => handleThemeChange('light')}
                >
                  <div className={styles.themePreview} data-theme="light">
                    <div className={styles.previewBar}></div>
                    <div className={styles.previewContent}>
                      <div className={styles.previewLine}></div>
                      <div className={styles.previewLine}></div>
                    </div>
                  </div>
                  <span className={styles.themeName}>Light</span>
                  {theme === 'light' && (
                    <svg className={styles.checkIcon} width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M13.485 1.431a1.473 1.473 0 012.104 2.062l-7.84 9.801a1.473 1.473 0 01-2.12.04L.431 8.138a1.473 1.473 0 012.084-2.083l4.111 4.112 6.82-8.69a.486.486 0 01.04-.045z"/>
                    </svg>
                  )}
                </button>
            </div>
          </div>
        );
      
      case 'profile':
        return (
          <UsageAnalytics />
        );
      
      case 'notifications':
        return (
          <div className={styles.generalContainer}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Notifications</h2>
            </div>
            <p className={styles.sectionDescription}>
              Configure notification preferences
            </p>
            <div className={styles.placeholderContent}>
              <p>Notification settings coming soon...</p>
            </div>
          </div>
        );
      
      case 'privacy':
        return (
          <div className={styles.generalContainer}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Privacy & Data</h2>
            </div>
            <p className={styles.sectionDescription}>
              Control your privacy and data settings
            </p>
            <div className={styles.placeholderContent}>
              <p>Privacy settings coming soon...</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>Settings</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" />
            </svg>
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.sidebar}>
            <button
              className={`${styles.sidebarItem} ${activeSection === 'general' ? styles.active : ''}`}
              onClick={() => setActiveSection('general')}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 9.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"/>
                <path fillRule="evenodd" d="M8 0a8 8 0 100 16A8 8 0 008 0zM1.5 8a6.5 6.5 0 1113 0 6.5 6.5 0 01-13 0z"/>
              </svg>
              <span>General</span>
            </button>

            <button
              className={`${styles.sidebarItem} ${activeSection === 'profile' ? styles.active : ''}`}
              onClick={() => setActiveSection('profile')}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 8a3 3 0 100-6 3 3 0 000 6zm2 1a3 3 0 00-2-0 3 3 0 00-2 0 5 5 0 00-4 4.76V15h12v-1.24A5 5 0 0010 9z"/>
              </svg>
              <span>Usage Analytics</span>
            </button>

            <button
              className={`${styles.sidebarItem} ${activeSection === 'notifications' ? styles.active : ''}`}
              onClick={() => setActiveSection('notifications')}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 16a2 2 0 001.985-1.75c.017-.137-.097-.25-.235-.25h-3.5c-.138 0-.252.113-.235.25A2 2 0 008 16z"/>
                <path d="M8 1.918l-.797.161A4.002 4.002 0 004 6c0 .628-.134 2.197-.459 3.742-.16.767-.376 1.566-.663 2.258h10.244c-.287-.692-.502-1.49-.663-2.258C12.134 8.197 12 6.628 12 6a4.002 4.002 0 00-3.203-3.92L8 1.917zM14.22 12c.223.447.481.801.78 1H1c.299-.199.557-.553.78-1C2.68 10.2 3 6.88 3 6c0-2.42 1.72-4.44 4.005-4.901a1 1 0 111.99 0A5.002 5.002 0 0113 6c0 .88.32 4.2 1.22 6z"/>
              </svg>
              <span>Notifications</span>
            </button>

            <button
              className={`${styles.sidebarItem} ${activeSection === 'privacy' ? styles.active : ''}`}
              onClick={() => setActiveSection('privacy')}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M5.338 1.59a61.44 61.44 0 00-2.837.856.481.481 0 00-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.725 10.725 0 002.287 2.233c.346.244.652.42.893.533.12.057.218.095.293.118a.55.55 0 00.101.025.615.615 0 00.1-.025c.076-.023.174-.061.294-.118.24-.113.547-.29.893-.533a10.726 10.726 0 002.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 00-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.29 8.531 1.067 8 1.067c-.53 0-1.552.223-2.662.524zM5.072.56C6.157.265 7.31 0 8 0s1.843.265 2.928.56c1.11.3 2.229.655 2.887.87a1.54 1.54 0 011.044 1.262c.596 4.477-.787 7.795-2.465 9.99a11.775 11.775 0 01-2.517 2.453 7.159 7.159 0 01-1.048.625c-.28.132-.581.24-.829.24s-.548-.108-.829-.24a7.158 7.158 0 01-1.048-.625 11.777 11.777 0 01-2.517-2.453C1.928 10.487.545 7.169 1.141 2.692A1.54 1.54 0 012.185 1.43 62.456 62.456 0 015.072.56z"/>
              </svg>
              <span>Privacy & Data</span>
            </button>
          </div>

          <div className={styles.content}>
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};
